import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { DoneMessage, ParticipantFields, GuardianFields, ConsentFields } from "../components/sponsored/SponsoredParts";

const P_FIELDS = ["first_name", "last_name", "dob", "gender", "tshirt_size", "shorts_size", "socks_size"];
const EMPTY_SIZES = { tshirt: [], shorts: [], socks: [] };

function formError(g, p, c) {
  if (!g.first_name || !g.last_name) return "Please complete guardian details.";
  if (!g.email) return "Guardian email is required.";
  if (P_FIELDS.some((f) => !p[f])) return "Please complete all participant fields.";
  if (!(c.guardian_confirm && c.terms && c.privacy && c.medical_share)) return "Please accept all required consents.";
  return null;
}

const merge = (setter) => (patch) => setter((x) => ({ ...x, ...patch }));

export default function SponsoredRegister() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [done, setDone] = useState(null);
  const [g, setG] = useState({});
  const [p, setP] = useState({ medical: { none: true, details: "" } });
  const [c, setC] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/sponsored/${token}`).then(({ data }) => {
      setCtx(data);
      if (data.already_completed) setDone("already");
      if (data.nominee) setP((x) => ({ ...x, first_name: data.nominee.first_name || "", last_name: data.nominee.last_name || "" }));
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invitation not found."));
  }, [token]);

  const submit = async () => {
    const err = formError(g, p, c);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/sponsored/${token}/complete`, { guardian: g, participant: p, consents: c });
      setDone(data.reference);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!ctx) return <div><Navbar /><div className="max-w-xl mx-auto px-4 py-24 text-center text-slate-500">Loading...</div></div>;
  if (done) return <div><Navbar /><DoneMessage done={done} /></div>;

  return (
    <div><Navbar />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="rounded-xl bg-[#F2F8F5] border border-[#008057]/20 p-4 mb-6">
          <div className="font-bold text-[#03281B]">Sponsored Registration - Paid</div>
          <p className="text-sm text-slate-600">A sponsor has covered the {ctx.category?.name} camp fee for your child. Please complete the details below - there is nothing to pay.</p>
        </div>
        <div className="rounded-2xl bg-white border border-[#E2EBF0] p-6 space-y-6" data-testid="sponsored-register-form">
          <ParticipantFields p={p} setP={merge(setP)} sizes={ctx.clothing_sizes || EMPTY_SIZES} />
          <GuardianFields setG={merge(setG)} />
          <ConsentFields c={c} setC={merge(setC)} />
          <button data-testid="sponsored-submit" onClick={submit} disabled={saving} className="w-full rounded-full bg-[#008057] text-white py-3 font-bold disabled:opacity-60">{saving ? "Submitting..." : "Complete Registration"}</button>
        </div>
      </div>
    </div>
  );
}
